import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  TablePagination,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { disableAttendance, refreshAttendance, activateAttendance } from "../../../services/mentorServices/MentorServices";
import { toast } from "react-toastify";
import ConfirmDisableModal from "../../ConfirmDisableModal";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    textTransform: "capitalize",
    fontSize: 16,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textTransform: "capitalize",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd),&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const AttendanceTable = ({
  attendanceRecords,
  handleEditClick,
  handleDownload,
  loadingData,
  setLoadingData,
  fetchAllAttendanceRecords,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDisableLab = (attendanceId) => {
    setSelectedAttendanceId(attendanceId);
    setModalOpen(true);
  };

  const confirmDisableLab = async () => {
    if (!selectedAttendanceId) return;

    try {
      const response = await disableAttendance(selectedAttendanceId);
      toast.success(response?.data.message);
      fetchAllAttendanceRecords();
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || error?.error);
    } finally {
      setModalOpen(false);
      setSelectedAttendanceId(null);
    }
  };

  const handleRefresh = async (row) => {
    try {
      setLoadingData(true);
      const response = await refreshAttendance(row?.dailyAttendanceId);
      await fetchAllAttendanceRecords();
      toast.success(response?.data?.message || "Attendance refreshed successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to refresh attendance. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleActivateAttendance = async (attendanceId) => {
    try {
      setLoadingData(true);
      const response = await activateAttendance(attendanceId);
      toast.success(response?.data?.message || "Attendance activated successfully.");
      fetchAllAttendanceRecords(); // refresh list
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to activate attendance.");
    } finally {
      setLoadingData(false);
    }
  };

  const paginatedRows = attendanceRecords?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto" }}>
      <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }} aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell>COE</StyledTableCell>
            <StyledTableCell>Lab Name</StyledTableCell>
            <StyledTableCell>Attendance Name</StyledTableCell>
            <StyledTableCell>Check-Out Status</StyledTableCell>
            <StyledTableCell>Geo-fencing</StyledTableCell>
            <StyledTableCell>Attendance Code</StyledTableCell>
            <StyledTableCell>Current Active</StyledTableCell> {/* New column */}
            <StyledTableCell>Practice Time</StyledTableCell>
            <StyledTableCell>Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingData ? (
            <TableRow>
              <StyledTableCell colSpan={9} align="center">
                <Box display="flex" justifyContent="center" alignItems="center" height="30px">
                  <CircularProgress />
                </Box>
              </StyledTableCell>
            </TableRow>
          ) : paginatedRows?.length > 0 ? (
            paginatedRows.map((row, index) => (
              <StyledTableRow key={index}>
                <StyledTableCell>{row.coe?.name || "-"}</StyledTableCell>
                <StyledTableCell>{row.lab?.lab_name || "-"}</StyledTableCell>
                <StyledTableCell>{row?.attendanceName || "-"}</StyledTableCell>
                <StyledTableCell>{row.isCheckoutEnabled ? "Enabled" : "Disabled"}</StyledTableCell>
                <StyledTableCell>{row.geo_fencing_allowed ? "True" : "False"}</StyledTableCell>
                <StyledTableCell>{row.attendanceCode || "-"}</StyledTableCell>

                {/* Current Active column */}
                <StyledTableCell>
                  {row.current_active ? (
                    <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        color: "#4c4e4cff",
                        borderColor: "#bbb",
                        borderRadius: "6px",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        padding: "2px 8px",
                        "&:hover": {
                          borderColor: "#888",
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                      onClick={() => handleActivateAttendance(row.dailyAttendanceId)}
                    >
                      Make Active
                    </Button>



                  )}
                </StyledTableCell>

                <StyledTableCell>{`${row?.sessionStartTime || ""} - ${row?.sessionEndTime || ""}`}</StyledTableCell>
                <StyledTableCell>
                  <Tooltip title="Edit" arrow>
                    <IconButton size="small" aria-label="edit" onClick={() => handleEditClick(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh Attendance" arrow>
                    <IconButton size="small" aria-label="refresh" onClick={() => handleRefresh(row)}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Disable" arrow>
                    <IconButton
                      size="small"
                      aria-label="disable"
                      onClick={() => handleDisableLab(row.dailyAttendanceId)}
                      disabled={row.disabled}
                    >
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download QR Code" arrow>
                    <IconButton size="small" aria-label="download" onClick={() => handleDownload(row)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </StyledTableCell>
              </StyledTableRow>
            ))
          ) : (
            <StyledTableRow>
              <StyledTableCell sx={{ fontWeight: "600", color: "grey" }} colSpan={9} align="center">
                No Attendance records found. Please add new Attendance to view them here.
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10,20,30,60,100]}
        component="div"
        count={attendanceRecords?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ConfirmDisableModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmDisableLab}
        message="Attendance Record"
      />
    </TableContainer>
  );
};

export default AttendanceTable;
